import React, { useEffect, useState } from "react";
import { useLang } from "../contexts/LanguageContext";
import { X, Globe2 } from "lucide-react";

const SUPPORTED = ["de", "en", "tr"];
const FLAGS = { de: "🇩🇪", en: "🇬🇧", tr: "🇹🇷" };
const NAMES = { de: "Deutsch", en: "English", tr: "Türkçe" };
const SUGGEST = {
  de: { msg: "Diese Seite ist auch auf Deutsch verfügbar.", cta: "Auf Deutsch ansehen", dismiss: "Ausblenden" },
  en: { msg: "This site is also available in English.", cta: "View in English", dismiss: "Dismiss" },
  tr: { msg: "Bu site Türkçe olarak da mevcut.", cta: "Türkçe görüntüle", dismiss: "Kapat" },
};
const DISMISS_KEY = "qrmp_lang_suggest_dismissed";
const PROMPTED_KEY = "qrmp_lang_prompted";

/**
 * Non-forced language suggestion banner. Shows once if:
 *  - browser language is supported AND different from current site language
 *  - user has not dismissed before
 *  - LangRouteSync hasn't auto-set (we only suggest, never auto-switch)
 */
export default function LanguageSuggestionBanner() {
  const { lang, setLang } = useLang();
  const [suggest, setSuggest] = useState(null);

  useEffect(() => {
    let dismissed = false;
    try { dismissed = localStorage.getItem(DISMISS_KEY) === "1"; } catch (e) {}
    if (dismissed) return;

    const navLang = (navigator.language || "").slice(0, 2).toLowerCase();
    if (!SUPPORTED.includes(navLang)) return;
    if (navLang === lang) return; // already matches

    // Small delay so it doesn't flash during first render
    const t = setTimeout(() => setSuggest(navLang), 1500);
    return () => clearTimeout(t);
  }, [lang]);

  if (!suggest) return null;
  const copy = SUGGEST[suggest] || SUGGEST.en;

  const accept = () => {
    setLang(suggest);
    try {
      localStorage.setItem("qrmp_lang", suggest);
      localStorage.setItem(DISMISS_KEY, "1");
      localStorage.setItem(PROMPTED_KEY, "1");
    } catch (e) {}
    setSuggest(null);
  };

  const dismiss = () => {
    try { localStorage.setItem(DISMISS_KEY, "1"); } catch (e) {}
    setSuggest(null);
  };

  return (
    <div
      className="fixed top-16 right-4 z-[55] max-w-sm rounded-2xl border border-purple-500/30 bg-white dark:bg-zinc-900 shadow-2xl shadow-purple-500/20 p-4 animate-in slide-in-from-right-4 fade-in duration-300"
      role="dialog"
      aria-live="polite"
      data-testid="lang-suggest-banner"
    >
      <button
        onClick={dismiss}
        aria-label="dismiss"
        className="absolute top-2 right-2 w-7 h-7 rounded-full grid place-items-center text-zinc-400 hover:text-zinc-900 dark:hover:text-white hover:bg-zinc-100 dark:hover:bg-zinc-800 transition-colors"
        data-testid="lang-suggest-dismiss"
      >
        <X className="h-3.5 w-3.5" />
      </button>
      <div className="flex items-start gap-3">
        <div className="w-9 h-9 rounded-xl bg-purple-500/15 text-purple-600 dark:text-purple-400 grid place-items-center shrink-0">
          <Globe2 className="h-5 w-5" />
        </div>
        <div className="flex-1 min-w-0 pr-5">
          <div className="text-sm font-bold text-zinc-900 dark:text-zinc-100">{FLAGS[suggest]} {NAMES[suggest]}</div>
          <p className="mt-1 text-xs text-zinc-600 dark:text-zinc-400 leading-snug">{copy.msg}</p>
          <div className="mt-3 flex gap-2">
            <button
              onClick={accept}
              className="inline-flex items-center px-3 py-1.5 rounded-full bg-purple-600 hover:bg-purple-500 text-white text-xs font-bold transition-colors"
              data-testid="lang-suggest-accept"
            >
              {copy.cta} →
            </button>
            <button
              onClick={dismiss}
              className="px-3 py-1.5 rounded-full text-xs font-semibold text-zinc-500 hover:text-zinc-900 dark:hover:text-zinc-200 transition-colors"
            >
              {copy.dismiss}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
